import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Spin, Alert, Typography, Button, Descriptions, Card, Row, Col, Input, Form, message, Select, Divider, Modal, Popconfirm, Tag } from 'antd';
import MomentDatePicker from './MomentDatePicker.tsx';
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { usePatientChart } from '../context/PatientChartContext';
import { useTranslation } from 'react-i18next';
import PatientPortalAccessManager from './PatientPortalAccessManager';
import HealthSurveyButton from './HealthSurveyButton';
import CareTeamManagementModal from './CareTeamManagementModal';

const { Title } = Typography;
const { Option } = Select;

const DemographicsSection = ({ patientId }) => {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, token } = useAuth();
  const { refreshPatientList } = usePatientChart();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // Helper function to get tier color and style
  const getTierDisplay = (tier) => {
    if (!tier) return null;

    const tierConfig = {
      'Planning': { color: '#8c8c8c', backgroundColor: '#f5f5f5', borderColor: '#d9d9d9' },
      'Essential': { color: '#52c41a', backgroundColor: '#f6ffed', borderColor: '#b7eb8f' },
      'Plus': { color: '#1890ff', backgroundColor: '#e6f7ff', borderColor: '#91d5ff' },
      'Premium': { color: '#722ed1', backgroundColor: '#f9f0ff', borderColor: '#d3adf7' }
    };

    const config = tierConfig[tier];
    if (!config) return <Tag>{tier}</Tag>;

    return (
      <Tag
        style={{
          color: config.color,
          backgroundColor: config.backgroundColor,
          borderColor: config.borderColor,
          fontWeight: 'bold'
        }}
      >
        {tier}
      </Tag>
    );
  };

  const [isEditingDemographics, setIsEditingDemographics] = useState(false);
  const [mdUsers, setMdUsers] = useState([]);
  const [nurseUsers, setNurseUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Care Team Management Modal state
  const [isCareTeamModalVisible, setIsCareTeamModalVisible] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    const loadData = async () => {
      if (!patientId) {
        if (isMounted) { setError("No patient selected."); setLoading(false); }
        return;
      }
      try {
        const patientRes = await axios.get(`/patients/${patientId}?populate=primaryMD,primaryNurse`);
        if (isMounted) { setPatient(patientRes.data.data.patient); setError(null); }
      } catch (err) {
        if (isMounted) { setError(err.response?.data?.message || `Failed to load patient data.`); }
      } finally {
        if (isMounted) { setLoading(false); }
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [patientId]);

  useEffect(() => {
    if (isEditingDemographics) {
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const config = { headers: { Authorization: `Bearer ${token}` } };
          const response = await axios.get('/users', config);
          if (response.data && response.data.data && response.data.data.users) {
            const allUsers = response.data.data.users;
            setMdUsers(allUsers.filter(u => u.role === 'MD'));
            setNurseUsers(allUsers.filter(u => u.role === 'Nursing'));
          }
        } catch (err) { message.error("Failed to load MD and Nurse users"); }
        finally { setLoadingUsers(false); }
      };
      fetchUsers();
    }
  }, [isEditingDemographics, token]);

  useEffect(() => {
    if (patient) {
      const dobMoment = patient.dateOfBirth ? moment(patient.dateOfBirth) : null;
      const care360StartDateMoment = patient.care360StartDate ? moment(patient.care360StartDate) : null;
      const initialValues = {
        firstName: patient.firstName || '',
        middleName: patient.middleName || '',
        lastName: patient.lastName || '',
        dateOfBirth: dobMoment && dobMoment.isValid() ? dobMoment : null,
        sex: patient.sex || '',
        race: patient.demographics?.race || undefined,
        relationshipStatus: patient.structuredLifestyleHistory?.relationshipStatus || patient.maritalStatus || '',
        care360StartDate: care360StartDateMoment && care360StartDateMoment.isValid() ? care360StartDateMoment : null,
        care360Tier: patient.care360Tier || undefined,
        primaryMD: patient.primaryMD?._id || undefined, primaryNurse: patient.primaryNurse?._id || undefined,
        contactInfoPhone: patient.contactInfo?.phone || '', contactInfoEmail: patient.contactInfo?.email || '',
        contactInfoStreet: patient.contactInfo?.address?.street || '', contactInfoCity: patient.contactInfo?.address?.city || '',
        contactInfoState: patient.contactInfo?.address?.state || '',
        emergencyContactName: patient.emergencyContact?.name || '',
        emergencyContactRelationship: patient.emergencyContact?.relationship || '',
        emergencyContactPhone: patient.emergencyContact?.phone || '',
      };
      form.setFieldsValue(initialValues);
    }
  }, [patient, isEditingDemographics, form]);

  const handleSaveDemographics = async (values) => {
    message.loading({ content: 'Saving demographics...', key: 'saveDemo' });
    try {
        // Fetch the latest patient data to ensure we have current structuredLifestyleHistory
        const freshPatientRes = await axios.get(`/patients/${patientId}`);
        const freshPatient = freshPatientRes.data.data.patient;
        
        const payload = {
            firstName: values.firstName,
            middleName: values.middleName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth ? moment(values.dateOfBirth).toISOString() : null,
            sex: values.sex,
            demographics: {
              ...(patient.demographics || {}),
              race: values.race,
            },
            // relationshipStatus is now handled in structuredLifestyleHistory below
            care360StartDate: values.care360StartDate ? moment(values.care360StartDate).toISOString() : null,
            care360Tier: values.care360Tier || null,
            primaryMD: values.primaryMD || null, primaryNurse: values.primaryNurse || null,
            contactInfo: {
                phone: values.contactInfoPhone, email: values.contactInfoEmail,
                address: { street: values.contactInfoStreet, city: values.contactInfoCity, state: values.contactInfoState, }
            },
            emergencyContact: { name: values.emergencyContactName, relationship: values.emergencyContactRelationship, phone: values.emergencyContactPhone },
            // Use fresh patient data to preserve all lifestyle history data, only update relationshipStatus
            structuredLifestyleHistory: { ...(freshPatient.structuredLifestyleHistory || {}), relationshipStatus: values.relationshipStatus }
        };
        const config = {}; if (user && user.token) { config.headers = { Authorization: `Bearer ${user.token}` }; }
        const response = await axios.patch(`/patients/${patientId}`, payload, config);
        const updatedPatient = response.data.data.patient;
        setPatient(updatedPatient);
        setIsEditingDemographics(false);
        message.success({ content: 'Demographics saved successfully!', key: 'saveDemo', duration: 2 });
        
        // Trigger patient list refresh to update displayed data
        if (refreshPatientList) {
          refreshPatientList();
        }
    } catch (error) {
        const errorMsg = error.response?.data?.message || 'Failed to save demographics.';
        message.error({ content: `Error: ${errorMsg}`, key: 'saveDemo', duration: 4 });
    }
  };

  const handleCancelEditDemographics = () => { setIsEditingDemographics(false); };

  const showDeletePasswordModal = () => {
    setDeletePassword('');
    setIsPasswordModalVisible(true);
  };

  const handleDeletePatient = async () => {
    if (!deletePassword) {
      message.error('Password is required to delete a patient.');
      return;
    }
    setDeleteLoading(true);
    try {
      if (!token) {
        message.error('Authentication required to delete patient.');
        setDeleteLoading(false);
        setIsPasswordModalVisible(false);
        return;
      }
      message.loading({ content: 'Deleting patient...', key: 'deletePatient' });
      const deleteConfig = {
        headers: { 'Authorization': `Bearer ${token}` },
        data: { password: deletePassword }
      };
      await axios.delete(`/patients/${patientId}`, deleteConfig);
      message.success({ content: 'Patient deleted successfully!', key: 'deletePatient', duration: 2 });
      setIsPasswordModalVisible(false);
      setDeletePassword('');
      setDeleteLoading(false);
      // Navigate back to patient list after showing success message
      setTimeout(() => {
        window.location.href = '/patients';
      }, 2000);
    } catch (error) {
      setDeleteLoading(false);
      if (error.response?.status === 403) {
        message.error({ content: 'You do not have permission to delete patients.', key: 'deletePatient', duration: 4 });
      } else if (error.response?.status === 401) {
        message.error({ content: 'Incorrect password.', key: 'deletePatient', duration: 4 });
      } else {
        const errorMsg = error.response?.data?.message || 'Failed to delete patient.';
        message.error({ content: `Error: ${errorMsg}`, key: 'deletePatient', duration: 4 });
      }
    }
  };

  // Care Team Management Modal handlers
  const handleCareTeamModalOpen = () => {
    setIsCareTeamModalVisible(true);
  };

  const handleCareTeamModalCancel = () => {
    setIsCareTeamModalVisible(false);
  };

  const handleCareTeamUpdate = () => {
    // Refresh patient data to reflect care team changes
    // The useEffect will automatically reload patient data when patientId changes
    // For immediate refresh, we could add a refresh function here if needed
    console.log('Care team updated for patient:', patientId);
  };

  // Allow all authenticated users to manage care team
  const canManageCareTeam = user;

  if (loading) { return ( <div style={{ textAlign: 'center', marginTop: '50px' }}><Spin size="large" /></div> ); }
  if (error) { return <Alert message={`Error loading chart: ${error}`} type="error" showIcon />; }
  if (!patient) { return <Alert message="Patient data could not be loaded." type="warning" showIcon />; }

  return (
    <div style={{ padding: '1px' }}>
      <div style={{ marginBottom: '16px', textAlign: 'right' }}>
        {!isEditingDemographics ? (
          <>
            <Button icon={<EditOutlined />} onClick={() => setIsEditingDemographics(true)} style={{ marginRight: 8 }}>
              {t('patientChart.demographics.editDemographics')}
            </Button>
            {canManageCareTeam && (
              <Button
                icon={<TeamOutlined />}
                onClick={handleCareTeamModalOpen}
                style={{ marginRight: 8 }}
              >
                Manage Care Team
              </Button>
            )}
            {user && user.role === 'Admin' && (
              <Popconfirm
                title="Are you sure you want to delete this patient?"
                description="This action cannot be undone. You will need to enter your password to confirm."
                onConfirm={showDeletePasswordModal}
                okText="Yes"
                cancelText="No"
              >
                <Button danger icon={<DeleteOutlined />}>
                  Delete Patient
                </Button>
              </Popconfirm>
            )}
          </>
        ) : (
          <>
            <Button icon={<CloseOutlined />} onClick={handleCancelEditDemographics} style={{ marginRight: 8 }}>{t('patientChart.demographics.cancelEdit')}</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => form.submit()}>{t('patientChart.demographics.saveDemographics')}</Button>
          </>
        )}
      </div>
      {!isEditingDemographics ? (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Card title="Patient Information" style={{ height: '100%' }}>
              <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} size="small">
                <Descriptions.Item label="First Name">{patient.firstName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Middle Name">{patient.middleName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Last Name">{patient.lastName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Date of Birth">{patient.dateOfBirth ? moment(patient.dateOfBirth).format('DD/MM/YYYY') : 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Sex">{patient.sex || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Race">{patient.demographics?.race || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Relationship Status">{patient.structuredLifestyleHistory?.relationshipStatus || patient.maritalStatus || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Care 360 Start Date">{patient.care360StartDate ? moment(patient.care360StartDate).format('DD/MM/YYYY') : 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Primary MD">{patient.primaryMD ? `${patient.primaryMD.name} (${patient.primaryMD.email})` : 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Care 360 Tier">
                  {patient.care360Tier ? getTierDisplay(patient.care360Tier) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Primary Nurse" span={2}>{patient.primaryNurse ? `${patient.primaryNurse.name} (${patient.primaryNurse.email})` : 'N/A'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Contact & Emergency" style={{ height: '100%' }}>
              <Title level={5}>Contact Information</Title>
              <Descriptions column={1} size="small" layout="horizontal" bordered>
                <Descriptions.Item label="Phone">{patient.contactInfo?.phone || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Email">{patient.contactInfo?.email || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Address">
                  {`${patient.contactInfo?.address?.street || ''}${patient.contactInfo?.address?.street ? ', ' : ''}${patient.contactInfo?.address?.city || ''}${patient.contactInfo?.address?.city ? ', ' : ''}${patient.contactInfo?.address?.state || ''}` || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
              <Divider />
              <Title level={5}>Emergency Contact</Title>
              <Descriptions column={1} size="small" layout="horizontal" bordered>
                <Descriptions.Item label="Name">{patient.emergencyContact?.name || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Relationship">{patient.emergencyContact?.relationship || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{patient.emergencyContact?.phone || 'N/A'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={24}>
            <HealthSurveyButton patient={patient} />
          </Col>
          <Col span={24}>
            <PatientPortalAccessManager
              patient={patient}
              onPasswordReset={(resetData) => {
                console.log('Portal password reset:', resetData);
              }}
            />
          </Col>
        </Row>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSaveDemographics}>
          <Title level={5}>Patient Information</Title>
          <Row gutter={16}>
            <Col span={8}><Form.Item label="First Name" name="firstName" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item label="Middle Name" name="middleName"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item label="Last Name" name="lastName" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item label="Date of Birth" name="dateOfBirth" rules={[{ required: true }]}><MomentDatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}>
              <Form.Item label="Sex" name="sex" rules={[{ required: true }]}>
                <Select placeholder="Select sex">
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Race" name="race">
                <Select placeholder="Select race" allowClear>
                  <Option value="White / Caucasian">White / Caucasian</Option>
                  <Option value="Black / African-American">Black / African-American</Option>
                  <Option value="Hispanic / Latino">Hispanic / Latino</Option>
                  <Option value="Northeast Asian">Northeast Asian</Option>
                  <Option value="Southeast Asian">Southeast Asian</Option>
                  <Option value="South Asian">South Asian</Option>
                  <Option value="Native American / Alaska Native">Native American / Alaska Native</Option>
                  <Option value="Native Hawaiian / Pacific Islander">Native Hawaiian / Pacific Islander</Option>
                  <Option value="Middle Eastern / North African">Middle Eastern / North African</Option>
                  <Option value="Other">Other</Option>
                  <Option value="Prefer not to say">Prefer not to say</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Relationship Status" name="relationshipStatus">
                <Select placeholder="Select relationship status" allowClear>
                  <Option value="Single">Single</Option>
                  <Option value="Married">Married</Option>
                  <Option value="Divorced">Divorced</Option>
                  <Option value="Widowed">Widowed</Option>
                  <Option value="Separated">Separated</Option>
                  <Option value="Domestic Partnership">Domestic Partnership</Option>
                  <Option value="In a Relationship">In a Relationship</Option>
                  <Option value="Prefer not to say">Prefer not to say</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Care 360 Start Date" name="care360StartDate">
                <MomentDatePicker style={{ width: '100%' }} placeholder="Select Care 360 start date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Care 360 Tier" name="care360Tier">
                <Select placeholder="Select Care 360 tier" allowClear>
                  <Option value="Planning">Planning</Option>
                  <Option value="Essential">Essential</Option>
                  <Option value="Plus">Plus</Option>
                  <Option value="Premium">Premium</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}><Form.Item label="Primary MD" name="primaryMD"><Select placeholder="Select primary MD" loading={loadingUsers} allowClear>{mdUsers.map(md => (<Option key={md._id} value={md._id}>{md.name} ({md.email})</Option>))}</Select></Form.Item></Col>
            <Col span={12}><Form.Item label="Primary Nurse" name="primaryNurse"><Select placeholder="Select primary nurse" loading={loadingUsers} allowClear>{nurseUsers.map(nurse => (<Option key={nurse._id} value={nurse._id}>{nurse.name} ({nurse.email})</Option>))}</Select></Form.Item></Col>
          </Row>
          <Title level={5} style={{ marginTop: '16px' }}>Contact Information</Title>
          <Row gutter={16}>
            <Col span={12}><Form.Item label="Phone" name="contactInfoPhone"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item label="Email" name="contactInfoEmail" rules={[{ type: 'email' }]}><Input /></Form.Item></Col>
            <Col span={24}><Form.Item label="Street Address" name="contactInfoStreet"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item label="City" name="contactInfoCity"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item label="Province" name="contactInfoState"><Input /></Form.Item></Col>
          </Row>
          <Title level={5} style={{ marginTop: '16px' }}>Emergency Contact</Title>
          <Row gutter={16}>
            <Col span={12}><Form.Item label="Emergency Contact Name" name="emergencyContactName"><Input placeholder="Enter emergency contact name" /></Form.Item></Col>
            <Col span={12}><Form.Item label="Relationship" name="emergencyContactRelationship"><Input placeholder="e.g., Spouse, Child, Parent, etc." /></Form.Item></Col>
            <Col span={12}><Form.Item label="Emergency Contact Phone" name="emergencyContactPhone"><Input placeholder="Enter emergency contact phone" /></Form.Item></Col>
          </Row>
          <Form.Item style={{ display: 'none' }}><Button type="primary" htmlType="submit">Submit</Button></Form.Item>
        </Form>
      )}

      {/* Password Confirmation Modal for Patient Deletion */}
      <Modal
        title="Confirm Patient Deletion"
        open={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPasswordModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={deleteLoading}
            onClick={handleDeletePatient}
          >
            Delete Patient
          </Button>
        ]}
      >
        <p>Are you sure you want to delete this patient? This action cannot be undone.</p>
        <p>Please enter your password to confirm:</p>
        <Input.Password
          placeholder="Enter your password"
          value={deletePassword}
          onChange={(e) => setDeletePassword(e.target.value)}
          onPressEnter={handleDeletePatient}
          style={{ marginTop: '10px' }}
        />
      </Modal>

      {/* Care Team Management Modal */}
      <CareTeamManagementModal
        visible={isCareTeamModalVisible}
        onCancel={handleCareTeamModalCancel}
        patientId={patientId}
        onCareTeamUpdate={handleCareTeamUpdate}
      />
    </div>
  );
};

export default DemographicsSection;
